import { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Printer, ArrowLeft, Building2, Download, Loader2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { DigitalResourceReceiptFields } from "./DigitalResourceReceiptFields";

/** Payload from GET /invoices/:id/receipt or admin receipt endpoint */
export interface InvoiceReceiptData {
  invoiceId: string;
  invoiceNumber: string;
  amountDue: number;
  amountPaid: number | null;
  schoolShare?: number | null;
  platformShare?: number | null;
  currency: string;
  paidAt: string | null;
  description: string;
  paymentReference?: string;
  patronName: string;
  patronEmail: string;
  patronPhone?: string | null;
  tenantName: string;
  /** From branding; displayed on receipt header when set. */
  libraryFullName?: string | null;
  libraryAddress?: string | null;
  tenantLogoUrl?: string | null;
  tenantPrimaryColor?: string | null;
  /** Signed URL for authorized signature image (tenant branding) */
  tenantReceiptSignatureUrl?: string | null;
  /** Signed URL for official stamp image (tenant branding) */
  tenantReceiptStampUrl?: string | null;
  /** Public URL (with integrity token) for QR verification */
  receiptVerificationUrl?: string | null;
  type?: string;
  /** Stable ID matching PDF watermark (digital_resource) */
  receiptLabel?: string;
  resourceTitle?: string;
  patronTypeAtPurchase?: string;
  patronTypeNameAtPurchase?: string;
  accessDurationDays?: number | null;
  accessExpiresAt?: string | null;
}

function currencySymbol(code: string): string {
  const c = (code || "NGN").toUpperCase();
  const map: Record<string, string> = {
    NGN: "₦",
    USD: "$",
    EUR: "€",
    GBP: "£",
  };
  return map[c] || `${c} `;
}

function formatMoney(amount: number, currency: string): string {
  const sym = currencySymbol(currency);
  return `${sym}${amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

/** html2canvas device-pixel ratio; 1.75 keeps text sharp without PNG-scale file bloat */
const RECEIPT_PDF_CAPTURE_SCALE = 1.75;
/**
 * Fake viewport so Tailwind `md:` rules match desktop during capture.
 * Otherwise phones capture the stacked mobile layout while the PDF page is landscape.
 */
const RECEIPT_PDF_WINDOW_WIDTH = 1280;
const RECEIPT_PDF_WINDOW_HEIGHT = 2200;
const RECEIPT_PDF_JPEG_QUALITY = 0.88;

export interface InvoiceReceiptDocumentProps {
  receipt: InvoiceReceiptData;
  backHref: string;
  backLabel: string;
}

/**
 * Line-item / total shown on the printed receipt: library (school) share only when
 * the invoice metadata includes a split; otherwise the full amount paid.
 */
export function receiptDisplayAmount(
  receipt: Pick<InvoiceReceiptData, "schoolShare" | "amountPaid" | "amountDue">
): number {
  const ss = receipt.schoolShare;
  if (ss != null && !Number.isNaN(Number(ss)) && Number(ss) > 0) {
    return Number(ss);
  }
  return receipt.amountPaid ?? receipt.amountDue ?? 0;
}

export function InvoiceReceiptDocument({
  receipt,
  backHref,
  backLabel,
}: InvoiceReceiptDocumentProps) {
  const captureRef = useRef<HTMLDivElement>(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const sym = currencySymbol(receipt.currency);
  const accent =
    receipt.tenantPrimaryColor && /^#[0-9A-Fa-f]{6}$/.test(receipt.tenantPrimaryColor)
      ? receipt.tenantPrimaryColor
      : undefined;

  const displayAmount = receiptDisplayAmount(receipt);
  const headerTitle = receipt.libraryFullName?.trim() || receipt.tenantName;
  const headerAddress = receipt.libraryAddress?.trim() || null;
  const showReceiptAuth =
    Boolean(receipt.tenantReceiptSignatureUrl) || Boolean(receipt.tenantReceiptStampUrl);
  const paidAtLabel = receipt.paidAt
    ? new Date(receipt.paidAt).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "—";

  const handleDownloadPdf = useCallback(async () => {
    const el = captureRef.current;
    if (!el) return;
    setPdfBusy(true);
    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);
      const canvas = await html2canvas(el, {
        scale: RECEIPT_PDF_CAPTURE_SCALE,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false,
        scrollX: 0,
        scrollY: 0,
        windowWidth: RECEIPT_PDF_WINDOW_WIDTH,
        windowHeight: RECEIPT_PDF_WINDOW_HEIGHT,
      });
      const imgData = canvas.toDataURL("image/jpeg", RECEIPT_PDF_JPEG_QUALITY);
      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const pxToMm = 25.4 / 96;
      const cssPxW = canvas.width / RECEIPT_PDF_CAPTURE_SCALE;
      const cssPxH = canvas.height / RECEIPT_PDF_CAPTURE_SCALE;
      const iw = cssPxW * pxToMm;
      const ih = cssPxH * pxToMm;
      const scale = Math.min(pageW / iw, pageH / ih, 1) * 0.96;
      const fw = iw * scale;
      const fh = ih * scale;
      pdf.addImage(imgData, "JPEG", (pageW - fw) / 2, (pageH - fh) / 2, fw, fh);
      pdf.save(`receipt-${receipt.invoiceNumber}.pdf`);
    } catch (e) {
      console.error("PDF export failed", e);
      window.print();
    } finally {
      setPdfBusy(false);
    }
  }, [receipt.invoiceNumber]);

  return (
    <>
      <style>
        {`
          @page { size: landscape; margin: 8mm; }
          @media print {
            .receipt-no-print { display: none !important; }
            .receipt-print-root { background: white !important; }
            .receipt-print-shell { background: white !important; padding: 0 !important; min-height: auto !important; }
            .receipt-print-card {
              box-shadow: none !important;
              border-radius: 0 !important;
              border: none !important;
              max-width: none !important;
            }
            .receipt-watermark-layer { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
          }
        `}
      </style>

      <div
        className={cn(
          "receipt-print-shell min-h-screen bg-muted/50 print:bg-white",
          "motion-reduce:transition-none"
        )}
      >
        <header className="receipt-no-print sticky top-0 z-20 border-b border-border/80 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
          <div className="mx-auto flex max-w-6xl flex-col gap-3 px-3 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3 sm:px-4">
            <Button
              variant="outline"
              size="sm"
              className="min-h-11 w-full justify-center gap-2 touch-manipulation sm:min-h-9 sm:w-auto"
              asChild
            >
              <Link to={backHref}>
                <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
                {backLabel}
              </Link>
            </Button>
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="min-h-11 w-full gap-2 touch-manipulation sm:min-h-9 sm:w-auto"
                disabled={pdfBusy}
                onClick={() => void handleDownloadPdf()}
              >
                {pdfBusy ? (
                  <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                ) : (
                  <Download className="h-4 w-4 shrink-0" aria-hidden />
                )}
                Download PDF
              </Button>
              <Button
                type="button"
                size="sm"
                className="min-h-11 w-full gap-2 touch-manipulation sm:min-h-9 sm:w-auto"
                onClick={() => window.print()}
              >
                <Printer className="h-4 w-4 shrink-0" aria-hidden />
                Print receipt
              </Button>
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-6xl px-3 py-6 sm:px-4 sm:py-8 print:max-w-none print:px-0 print:py-0">
          <article
            className={cn(
              "receipt-print-card receipt-print-root overflow-hidden rounded-2xl border border-border bg-card text-card-foreground shadow-sm print:shadow-none",
              "border-t-4 print:border-t-4",
              !accent && "border-t-primary"
            )}
            style={accent ? { borderTopColor: accent } : undefined}
          >
            <div ref={captureRef} className="relative overflow-hidden bg-card">
              {/* Tenant-name watermark */}
              <div
                className="receipt-watermark-layer pointer-events-none absolute inset-0 z-0 flex items-center justify-center overflow-hidden"
                aria-hidden
              >
                <span
                  className="whitespace-nowrap text-center text-[2.75rem] font-bold uppercase tracking-wider text-foreground/[0.07] sm:text-[3.25rem]"
                  style={{ transform: "rotate(-32deg) scale(1.15)" }}
                >
                  {headerTitle}
                </span>
              </div>

              <div className="relative z-[1] p-6 sm:p-8 print:p-4">
                <header className="mb-6 border-b border-border pb-5 print:mb-4">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex items-start gap-4">
                      {receipt.tenantLogoUrl ? (
                        <img
                          src={receipt.tenantLogoUrl}
                          alt={`${headerTitle} logo`}
                          className="h-16 max-w-[180px] object-contain object-left print:h-14"
                        />
                      ) : (
                        <div
                          className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl border border-border bg-muted/50 text-muted-foreground"
                          aria-hidden
                        >
                          <Building2 className="h-8 w-8" />
                        </div>
                      )}
                      <div>
                        <h1 className="text-xl font-bold tracking-tight text-foreground sm:text-2xl">
                          {headerTitle}
                        </h1>
                        {headerAddress ? (
                          <p className="mt-1 max-w-xl whitespace-pre-line text-sm italic text-muted-foreground">
                            {headerAddress}
                          </p>
                        ) : null}
                        <p className="mt-1 text-xs text-muted-foreground sm:text-sm">
                          Payment confirmation
                        </p>
                      </div>
                    </div>
                    <div className="text-left sm:text-right">
                      <p className="text-[0.65rem] font-bold uppercase tracking-[0.2em] text-muted-foreground">
                        Invoice transaction receipt
                      </p>
                      <p className="mt-1 font-mono text-sm text-muted-foreground">Page 1 of 1</p>
                      <p className="mt-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        Receipt no.
                      </p>
                      <p className="font-mono text-lg font-semibold text-foreground">
                        {receipt.invoiceNumber}
                      </p>
                    </div>
                  </div>
                </header>

                <div className="mb-6 grid gap-6 lg:grid-cols-[1fr_auto] print:grid-cols-[1fr_auto]">
                  <section className="rounded-lg border border-border/80 bg-muted/15 p-4 sm:p-5 print:border print:bg-transparent">
                    <h2 className="mb-3 border-b border-border/60 pb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      Payer information
                    </h2>
                    <dl className="grid gap-2 text-sm sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <dt className="text-xs font-medium text-muted-foreground">Full name</dt>
                        <dd className="font-medium text-foreground">{receipt.patronName}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-muted-foreground">Email</dt>
                        <dd className="break-all text-foreground">{receipt.patronEmail}</dd>
                      </div>
                      {receipt.patronPhone ? (
                        <div>
                          <dt className="text-xs font-medium text-muted-foreground">Phone</dt>
                          <dd className="text-foreground">{receipt.patronPhone}</dd>
                        </div>
                      ) : null}
                    </dl>
                  </section>

                  {receipt.receiptVerificationUrl ? (
                    <div className="mx-auto flex w-full max-w-[200px] flex-col items-center gap-2 rounded-lg border border-border/80 bg-muted/10 p-4 sm:mx-0 sm:w-40 sm:max-w-none print:w-36">
                      <p className="text-center text-[0.65rem] font-semibold uppercase tracking-wide text-muted-foreground">
                        Verify authenticity
                      </p>
                      <QRCodeSVG
                        value={receipt.receiptVerificationUrl}
                        size={112}
                        level="M"
                        includeMargin={false}
                        className="h-auto max-w-full"
                      />
                      <p className="text-center text-[0.6rem] leading-tight text-muted-foreground">
                        Scan to confirm this receipt on the official library site.
                      </p>
                    </div>
                  ) : null}
                </div>

                <section className="mb-6">
                  <h2 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Payment details
                  </h2>
                  {/* Mobile: stacked rows (no horizontal scroll) */}
                  <div className="space-y-3 md:hidden print:hidden">
                    <div className="rounded-lg border border-border/80 bg-muted/10 p-4">
                      <dl className="space-y-3 text-sm">
                        <div className="flex justify-between gap-4">
                          <dt className="text-muted-foreground">Invoice / order</dt>
                          <dd className="font-mono text-right text-xs text-foreground">
                            {receipt.invoiceNumber}
                          </dd>
                        </div>
                        <div className="flex justify-between gap-4">
                          <dt className="text-muted-foreground">Trans. ref.</dt>
                          <dd className="break-all text-right font-mono text-xs text-foreground">
                            {receipt.paymentReference || "—"}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground">Description</dt>
                          <dd className="mt-1 text-foreground">{receipt.description}</dd>
                        </div>
                        <DigitalResourceReceiptFields receipt={receipt} variant="rows" />
                        <div className="flex justify-between gap-4 border-t border-border/60 pt-3">
                          <dt className="font-medium text-foreground">Amount</dt>
                          <dd className="font-mono font-semibold tabular-nums text-foreground">
                            {formatMoney(displayAmount, receipt.currency)}
                          </dd>
                        </div>
                        <div className="flex justify-between gap-4">
                          <dt className="text-muted-foreground">Paid</dt>
                          <dd className="text-foreground">100%</dd>
                        </div>
                        <div className="flex justify-between gap-4">
                          <dt className="text-muted-foreground">Date</dt>
                          <dd className="text-right text-foreground">{paidAtLabel}</dd>
                        </div>
                      </dl>
                    </div>
                    <div className="flex justify-end border-t border-border pt-3">
                      <div className="text-right">
                        <p className="text-xs font-medium uppercase text-muted-foreground">Total</p>
                        <p className="font-mono text-lg font-semibold tabular-nums text-foreground">
                          {sym}
                          {displayAmount.toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2,
                          })}{" "}
                          <span className="text-sm font-normal text-muted-foreground">
                            {receipt.currency}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                  {/* Tablet+: full table; print always uses table */}
                  <div className="hidden overflow-x-auto md:block print:block">
                    <table className="w-full min-w-[640px] border-collapse border border-border text-sm print:min-w-0">
                      <thead>
                        <tr className="border-b border-border bg-muted/50 text-left text-[0.65rem] font-bold uppercase tracking-wide text-muted-foreground">
                          <th className="border-r border-border px-2 py-2">#</th>
                          <th className="border-r border-border px-2 py-2">Invoice / order ID</th>
                          <th className="border-r border-border px-2 py-2">Trans. ref. number</th>
                          <th className="border-r border-border px-2 py-2">Description</th>
                          <th className="border-r border-border px-2 py-2 text-right">Amount</th>
                          <th className="border-r border-border px-2 py-2 text-right">Paid %</th>
                          <th className="px-2 py-2 text-right">Transaction date</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-border">
                          <td className="border-r border-border px-2 py-3 align-top font-mono text-foreground">
                            1
                          </td>
                          <td
                            className="border-r border-border px-2 py-3 align-top font-mono text-xs text-foreground"
                            title={receipt.invoiceId}
                          >
                            {receipt.invoiceNumber}
                          </td>
                          <td className="border-r border-border px-2 py-3 align-top font-mono text-xs text-foreground">
                            {receipt.paymentReference || "—"}
                          </td>
                          <td className="border-r border-border px-2 py-3 align-top text-foreground">
                            {receipt.description}
                            <DigitalResourceReceiptFields receipt={receipt} />
                          </td>
                          <td className="border-r border-border px-2 py-3 align-top text-right font-medium tabular-nums text-foreground">
                            {formatMoney(displayAmount, receipt.currency)}
                          </td>
                          <td className="border-r border-border px-2 py-3 align-top text-right tabular-nums text-foreground">
                            100%
                          </td>
                          <td className="px-2 py-3 align-top text-right text-foreground">
                            {paidAtLabel}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-3 hidden flex-wrap justify-end gap-4 border-t border-border pt-3 md:flex print:flex">
                    <div className="text-right">
                      <p className="text-xs font-medium uppercase text-muted-foreground">Total</p>
                      <p className="font-mono text-lg font-semibold tabular-nums text-foreground">
                        {sym}
                        {displayAmount.toLocaleString(undefined, {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 2,
                        })}{" "}
                        <span className="text-sm font-normal text-muted-foreground">
                          {receipt.currency}
                        </span>
                      </p>
                    </div>
                  </div>
                </section>

                {showReceiptAuth ? (
                  <div className="mb-6 border-t border-border pt-6 print:mb-4">
                    <h2 className="mb-4 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      Official authorization
                    </h2>
                    {receipt.tenantReceiptSignatureUrl && receipt.tenantReceiptStampUrl ? (
                      <div className="flex flex-col items-start">
                        <p className="mb-2 text-xs text-muted-foreground">
                          Authorized signature with official stamp
                        </p>
                        <div className="relative inline-block max-w-[260px] print:max-w-[240px]">
                          <img
                            src={receipt.tenantReceiptSignatureUrl}
                            alt={`Authorized signature for ${headerTitle}`}
                            className="relative z-0 block max-h-24 max-w-[220px] object-contain object-left print:max-h-20"
                          />
                          <img
                            src={receipt.tenantReceiptStampUrl}
                            alt={`Official stamp for ${headerTitle}`}
                            className="pointer-events-none absolute left-1/2 top-1/2 z-10 max-h-24 max-w-[130px] -translate-x-1/2 -translate-y-1/2 object-contain opacity-90 print:max-h-20 print:max-w-[120px]"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-8">
                        {receipt.tenantReceiptSignatureUrl ? (
                          <div className="flex flex-col items-start">
                            <p className="mb-2 text-xs text-muted-foreground">
                              Authorized signature
                            </p>
                            <img
                              src={receipt.tenantReceiptSignatureUrl}
                              alt={`Authorized signature for ${headerTitle}`}
                              className="max-h-24 max-w-[220px] object-contain object-left print:max-h-20"
                            />
                          </div>
                        ) : null}
                        {receipt.tenantReceiptStampUrl ? (
                          <div className="flex flex-col items-start">
                            <p className="mb-2 text-xs text-muted-foreground">Official stamp</p>
                            <img
                              src={receipt.tenantReceiptStampUrl}
                              alt={`Official stamp for ${headerTitle}`}
                              className="max-h-28 max-w-[140px] object-contain object-left print:max-h-24"
                            />
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                ) : null}

                <footer className="border-t border-border pt-5 text-center text-xs text-muted-foreground print:pt-4">
                  This document confirms payment received by {headerTitle}. For support, contact
                  your library administration.
                </footer>
              </div>
            </div>
          </article>
        </div>
      </div>
    </>
  );
}
